import {React, observer, computed, useLocalStore, useAsObservableSource, Fragment, computedFn} from '@api'
import {BINDING_DISTANCE, DashStyle, DEFAULT_STROKE_WIDTH, GHOSTED_OPACITY, RectangleShape, TldrawApp, useTldrawApp} from '@modules/whiteboard'
import {SVGContainer, TLComponentProps, Utils} from '@tldraw/core'
import {Vec} from '@tldraw/vec'
import {getStroke, getStrokePoints} from 'perfect-freehand'
import { RectangleUtil } from '.'
import {ShapeComponentStore} from '@modules/whiteboard/shapes/ShapeComponentStore'
import * as css from './RectangleShapeComponent.css'

/// region Components

type IRectangleStroke = [number[], number[], number]

export class RectangleShapeStore extends ShapeComponentStore<RectangleShape, RectangleUtil> {
  constructor(public props: TLComponentProps<RectangleShape> & {app: TldrawApp}) {
    super(props)
  }

  get shape() { return this.props.shape}

  get style() { return this.shape.style}

  @computed get sw() {
    const {style: {strokeWidth}} = this
    return 1 + (strokeWidth ?? DEFAULT_STROKE_WIDTH) * 1.618
  }

  @computed get w() {
    const {sw, shape: {size}} = this
    return Math.max(0, size[0] - sw / 2)
  }

  @computed get h() {
    const {sw, shape: {size}} = this
    return Math.max(0, size[1] - sw / 2)
  }

  @computed get strokes() {
    const {sw, w, h, shape: {style: {dash}}} = this
    if (dash === DashStyle.Draw) return []

    const strokes: Array<IRectangleStroke> = [
      [[sw / 2, sw / 2], [w, sw / 2], w - sw / 2],
      [[w, sw / 2], [w, h], h - sw / 2],
      [[w, h], [sw / 2, h], w - sw / 2],
      [[sw / 2, h], [sw / 2, sw / 2], h - sw / 2]
    ]

    return strokes
  }

  strokePathFor = computedFn((stroke: IRectangleStroke) => {
    const [start, end, length] = stroke,
      {style: {dash, strokeWidth}} = this

    return Utils.getPerfectDashProps(
      length,
      strokeWidth * 1.618,
      dash
    )
  })

  @computed get dragSelectStroke() {
    return 'var(--tl-selectStroke)'
  }

  @computed get outerRectangleBorderLines() {
    const {dragSelectStroke, sw, strokes, shape: {id, style: {strokeColor}}, strokePathFor, app: {isInSession}} = this
    const paths = strokes.map((s, i) => {
      const {strokeDasharray, strokeDashoffset} = strokePathFor(s),
        [start, end] = s
      return (
        <line
          key={`${id}_${i}`}
          x1={start[0]}
          y1={start[1]}
          x2={end[0]}
          y2={end[1]}
          stroke={isInSession ? strokeColor : dragSelectStroke}
          strokeWidth={sw}
          strokeLinecap='round'
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
        />
      )
    })

    return paths
  }

  @computed get rectangleDrawPoints() {
    const {shape: {id, size}, style: {strokeWidth: sw}} = this,
      getRandom = Utils.rng(id)

    // Dimensions
    const w = Math.max(0, size[0]),
      h = Math.max(0, size[1])

    // Random corner offsets
    const offsets = Array.from(Array(4)).map(() => {
      return [getRandom() * sw * 0.75, getRandom() * sw * 0.75]
    })

    // Corners
    const tl = Vec.add([sw / 2, sw / 2], offsets[0])
    const tr = Vec.add([w - sw / 2, sw / 2], offsets[1])
    const br = Vec.add([w - sw / 2, h - sw / 2], offsets[2])
    const bl = Vec.add([sw / 2, h - sw / 2], offsets[3])

    // Which side to start drawing first
    const rm = Math.round(Math.abs(getRandom() * 2 * 4))

    // Corner radii
    const rx = Math.min(w / 2, sw * 2)
    const ry = Math.min(h / 2, sw * 2)

    // Number of points per side
    const px = Math.max(8, Math.floor(w / 16))
    const py = Math.max(8, Math.floor(h / 16))

    // Inset each line by the corner radii and let the freehand algo
    // interpolate points for the corners.
    const lines = Utils.rotateArray(
      [
        Vec.pointsBetween(Vec.add(tl, [rx, 0]), Vec.sub(tr, [rx, 0]), px),
        Vec.pointsBetween(Vec.add(tr, [0, ry]), Vec.sub(br, [0, ry]), py),
        Vec.pointsBetween(Vec.sub(br, [rx, 0]), Vec.add(bl, [rx, 0]), px),
        Vec.pointsBetween(Vec.sub(bl, [0, ry]), Vec.add(tl, [0, ry]), py),
      ],
      rm
    )

    // For the final points, include the first half of the first line again,
    // so that the line wraps around and avoids ending on a sharp corner.
    // This has a bit of finesse and magic—if you change the points between
    // function, then you'll likely need to change this one too.

    const points = [...lines.flat(), ...lines[0]].slice(
      5,
      Math.floor((rm % 2 === 0 ? px : py) / -2) + 3
    )

    return points
  }


  @computed get strokeInfo() {
    const {style: {strokeWidth}, rectangleDrawPoints: points} = this

    const options = {
      size: strokeWidth,
      thinning: 0.65,
      streamline: 0.3,
      smoothing: 1,
      simulatePressure: false,
      last: true,
    }

    return { points, options }
  }

  @computed get pathStroke() {
    const { strokeInfo: { points, options }} = this

    return getStroke(points, options)
  }

  @computed get rectanglePath() {
    return Utils.getSvgPathFromStroke(this.pathStroke)
  }

  @computed get indicatorPath() {
    const { strokeInfo: { points, options }} = this
    const strokePoints = getStrokePoints(points, options)

    return Utils.getSvgPathFromStroke(
      strokePoints.map((pt) => pt.point.slice(0, 2)),
      false
    )
  }
}

export const RectangleShapeComponent = observer<TLComponentProps<RectangleShape>, any>(function RectangleShapeComponent(props, ref) {
  const app = useTldrawApp(),
    obsProps = useAsObservableSource({...props, app}),
    store = useLocalStore(() => new RectangleShapeStore(obsProps)),
    {id, shape: {style: {strokeWidth, strokeColor, backgroundColor, isFilled}}, props: {shape, isGhost, isBinding, events, app: {isInSession}}} = store,
    {size, style} = shape

  if (shape.type != 'rectangle') return null

  if (style.dash === DashStyle.Draw) {
      const x = strokeWidth / 2 - BINDING_DISTANCE,
        {rectanglePath, indicatorPath} = store

    return (
      // <div className={css.root} ref={ref} style={{width, height, left: x, top: x}}>
      <Fragment>
        <SVGContainer id={id} key={shape.type} className={css.svgContainer} ref={ref} {...events}>
          {isBinding && (
            <rect
              className='tl-binding-indicator'
              x={x}
              y={x}
              width={Math.max(0, size[0] - strokeWidth / 2) + BINDING_DISTANCE * 2}
              height={Math.max(0, size[1] - strokeWidth / 2) + BINDING_DISTANCE * 2}
            />
          )}
          <path
            d={indicatorPath}
            fill={backgroundColor ?? 'none'}
            radius={strokeWidth}
            stroke={'none'}
            pointerEvents='all'
          />
          <path
            d={rectanglePath}
            fill={strokeColor}
            stroke={isInSession ? 'var(--tl-selectStroke)' : strokeColor}
            strokeWidth={strokeWidth}
            pointerEvents='all'
            opacity={isGhost ? GHOSTED_OPACITY : 1}
          />
        </SVGContainer>
      </Fragment>
    )
  }

  const {sw, w, h, outerRectangleBorderLines} = store
  const x = sw / 2, y = x

  return (
    <Fragment>
      <SVGContainer id={shape.id + '_svg'} className={css.svgContainer} ref={ref} {...events}>
        {isBinding && (
          <rect
            className='tl-binding-indicator'
            x={sw / 2 - 32}
            y={sw / 2 - 32}
            width={w + 64}
            height={h + 64}
          />
        )}
        <rect
          x={x}
          y={y}
          width={w}
          height={h}
          fill={backgroundColor}
          strokeWidth={sw}
          stroke='none'
          pointerEvents='all'
        />
        <g pointerEvents='stroke'>{outerRectangleBorderLines}</g>
      </SVGContainer>
    </Fragment>
  )
}, {forwardRef: true})

/// endregion




// WEBPACK FOOTER //
// ./src/modules/whiteboard/shapes/rectangle/RectangleShapeComponent.tsx